import { Button, Flex, Stack, useDisclosure } from '@chakra-ui/react';
import { getTenantByHost } from '@pelicargo/tenants';
import { TSContact } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { onboardingStepOptions } from '@pelicargo/utils';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { useDashboard } from '../../hooks/useDashboard';
import { useTypesense } from '../../hooks/useTypesense';
import { AlertModal } from '../AlertModal/AlertModal';
import { Label } from '../LabeledText';
import { SetTempPasswordModal } from '../SetTempPasswordModal';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { TypesenseSelect } from '../TypesenseSelect';

type Props = {
  userId?: number | null;
  authId?: string | null;
  onClose?: () => void;
};

export const UserForm = ({ userId, authId, onClose }: Props) => {
  const { control, setValue } = useFormContext();
  const { typesenseClient } = useTypesense();

  const contactId = useWatch({
    control,
    name: 'contact_id',
  });
  const [contactInfo, setContactInfo] = useState<TSContact>();

  useEffect(() => {
    (async () => {
      const tenant = getTenantByHost();

      const q = await typesenseClient
        .collections('contacts')
        .documents()
        .search({
          q: '*',
          filter_by: `id:=${contactId},tenant_ids:[${tenant.id}]`,
        });
      setContactInfo(q.hits?.[0]?.document as TSContact);
    })();
  }, [contactId, typesenseClient]);

  const { isDevMode } = useDashboard();
  const showSetTempPassword = isDevMode && !!authId;
  const tempPasswordModal = useDisclosure();

  const deactivateModal = useDisclosure();
  const { mutateAsync: deactivateUser, isLoading: isDeactivating } = trpc.deactivateUser.useMutation();

  const deactivate = async () => {
    await deactivateUser({
      user_id: userId,
    });
    deactivateModal.onClose();
    onClose();
  };

  return (
    <Stack direction="column" spacing={4} w="full">
      <Stack direction={{ base: 'column', md: 'row' }} w="full">
        <SimpleInput name="full_name" label="Full Name" placeholder="" isRequired />
        <SimpleInput name="preferred_name" label="Preferred Name" placeholder="" isRequired />
      </Stack>
      <SimpleInput name="email" label="Email" placeholder="someone@awesomecompany.com" isRequired />
      <SimpleInput name="phone_number" label="Phone" placeholder="Call me maybe?" />
      <SimpleInput name="title" label="Title" placeholder="Title" />
      <Label label="Preferred Account" />
      <TypesenseSelect
        name="preferred_account_id"
        collection="accounts"
        queryBy="name,account_id"
        documentLabel="name"
        documentValue="account_id"
        suffixWithValue
        onChange={({ value }) => {
          setValue('preferred_account_id', value);
        }}
        shouldProcessValue
      />
      <Flex flexDir="column" w="full">
        <Label label="Contact" />

        <TypesenseSelect
          collection="contacts"
          value={{
            value: contactInfo?.id,
            name_label: contactInfo?.full_name,
            label: `${contactInfo?.full_name ?? contactInfo?.preferred_name} (${contactInfo?.id})`,
          }}
          name="contact_id"
          queryBy="preferred_name,full_name"
          documentLabel="full_name"
          documentValue={'id'}
          suffixWithValue
          onChange={({ value }) => {
            setValue('contact_id', value);
          }}
        />
      </Flex>
      <SimpleReactSelect name="onboarding_step" label="Onboarding Step" isSearchable options={onboardingStepOptions} />
      <Flex>
        <Flex gap={2}>
          <Button colorScheme="red" onClick={deactivateModal.onToggle}>
            Deactivate User
          </Button>
          <Show if={showSetTempPassword}>
            <Button colorScheme="orange" onClick={tempPasswordModal.onOpen}>
              Set Temp Password
            </Button>
          </Show>
        </Flex>
      </Flex>
      <SimpleCheckbox name="isVerified" label="Is Email Verified" />
      <AlertModal
        isOpen={deactivateModal.isOpen}
        onClose={deactivateModal.onClose}
        title="Deactivate this user?"
        message="Are you sure? This will completely lock this user out of logging in and is very difficult to revert. This action will be logged."
        btnCancel="Cancel"
        btnDelete="Delete"
        handleConfirm={deactivate}
        isDeleting={isDeactivating}
      />
      <Show if={showSetTempPassword}>
        <SetTempPasswordModal authId={authId} isOpen={tempPasswordModal.isOpen} onClose={tempPasswordModal.onClose} />
      </Show>
    </Stack>
  );
};
